import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAudio } from './AudioContext';

const DynamicThemeContext = createContext();

// Default logo - update this to your radio logo
const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_0f6d5a99-b561-454b-bc4e-ddaee0ea145c/artifacts/i142ayxr_LOGO%20MAJESTIC%20JUKEBOX%20RADIO%20OK.jpg';

export const DynamicThemeProvider = ({ children }) => {
  const { currentTrack } = useAudio();
  const [themeColors, setThemeColors] = useState({
    primary: '#4169E1',
    secondary: '#00ff7f',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #4169E1 100%)'
  });
  const [displayArtwork, setDisplayArtwork] = useState(DEFAULT_LOGO);

  // Extract colors from album artwork
  const extractColorsFromImage = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      // Use proxy for external images via Netlify Function
      const proxyUrl = imageUrl.includes('itunes.apple.com') 
        ? `/.netlify/functions/proxy-image?url=${encodeURIComponent(imageUrl)}`
        : imageUrl;
      
      img.src = proxyUrl;
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          // Sample colors from the image
          const colorMap = {};
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const brightness = (r + g + b) / 3;
            
            // Skip very dark or very light colors
            if (brightness > 30 && brightness < 200) {
              const rgb = `${r},${g},${b}`;
              colorMap[rgb] = (colorMap[rgb] || 0) + 1;
            }
          }
          
          // Find dominant color
          const sortedColors = Object.entries(colorMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);
          
          if (sortedColors.length > 0) {
            const [r, g, b] = sortedColors[0][0].split(',').map(Number);
            const dominantColor = `rgb(${r}, ${g}, ${b})`;
            
            resolve({
              primary: dominantColor,
              background: `linear-gradient(135deg, rgba(${r}, ${g}, ${b}, 0.3) 0%, #1a1a2e 50%, #4169E1 100%)`
            });
          } else {
            resolve(null);
          }
        } catch (err) {
          console.error('Error extracting colors:', err);
          resolve(null);
        }
      };
      
      img.onerror = () => {
        console.error('Error loading image for color extraction');
        resolve(null);
      };
    });
  };

  // Update theme when artwork changes
  useEffect(() => {
    const updateTheme = async () => {
      const artwork = currentTrack.artwork || DEFAULT_LOGO;
      setDisplayArtwork(artwork);
      
      if (currentTrack.artwork) {
        const colors = await extractColorsFromImage(currentTrack.artwork);
        if (colors) {
          setThemeColors({
            primary: colors.primary,
            secondary: '#00ff7f',
            background: colors.background
          });
        }
      } else {
        // Reset to default theme when no artwork
        setThemeColors({
          primary: '#4169E1',
          secondary: '#00ff7f',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #4169E1 100%)'
        });
      }
    };
    
    updateTheme();
  }, [currentTrack.artwork]);

  const value = {
    themeColors,
    displayArtwork
  };

  return <DynamicThemeContext.Provider value={value}>{children}</DynamicThemeContext.Provider>;
};

export const useDynamicTheme = () => {
  const context = useContext(DynamicThemeContext);
  if (!context) {
    throw new Error('useDynamicTheme must be used within DynamicThemeProvider');
  }
  return context;
};